'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useSwrApi, useSwrMutationApi } from '@hooks/useSwrApi';
import { useSwrGlobal } from '@hooks/useSwrApi/useSwrGlobal';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { RedirectCountdownModal } from '@components/features/modals/redirects/RedirectCountdownModal';
import { Button, Modal, TailwindSpinner } from '@components/common';
import {
  getBundleDisplayTitleV2,
  getDisplayBundlesForJobAddV2,
} from '@components/templates/job-add-v2/form/JobBundleSelectionModalV2';

/**
 * Why the dialog was opened, when it was not the client choosing to upgrade.
 * Drives an explanatory line above the packages — someone who arrived here by
 * clicking "contact" needs to be told what upgrading has to do with it.
 */
export type UpgradeJobReason = 'contact-applicant';

interface UpgradeJobDialogProps {
  open: boolean;
  onClose: () => void;
  jobSlug: string;
  onSuccess?: () => void;
  reason?: UpgradeJobReason;
}

const getBenefitTitle = (benefit: ApiSchema<'BenefitItem'> | string): string => {
  if (typeof benefit === 'string') return benefit;
  return benefit?.title || '';
};

const toRoundedPrice = (price?: number): number => {
  if (typeof price !== 'number' || Number.isNaN(price)) return 0;
  return Math.max(0, Math.ceil(price));
};

export const UpgradeJobDialog = (props: UpgradeJobDialogProps) => {
  const {
    open,
    onClose,
    jobSlug,
    onSuccess,
    reason,
  } = props;

  const { t } = useTranslation('common');
  const { locale = 'ro', router } = useExtendedRouter();
  const { clearCacheKeysIncludes } = useSwrGlobal();

  // The tab the client has picked in the Alege-cum-recrutezi switcher
  // (freemium / jobless activations only — see showHireTabs below). Default
  // false = Recrutăm pentru tine, matching the Job Add default. undefined
  // means "let the server pick", so the first fetch has no `?withoutHire`
  // override and the server derivation applies.
  const [withoutHire, setWithoutHire] = useState<boolean | undefined>(undefined);

  const upgradeBundlesQuery = withoutHire === undefined ? '' : `?withoutHire=${withoutHire ? '1' : '0'}`;

  const { data: upgradeBundlesData, isValidating } = useSwrApi<{
    bundles?: ApiSchema<'ActivationBundle'>[];
    unlockOnly?: boolean;
    jobTitle?: string;
    showHireTabs?: boolean;
  }>({
    url: `/v1/my-jobs/${jobSlug}/upgrade-bundles${upgradeBundlesQuery}`,
    locale,
    shouldFetch: open,
    apiOptions: {
      revalidateOnFocus: false,
      keepPreviousData: true,
    },
  });

  const showHireTabs = Boolean(upgradeBundlesData?.showHireTabs);
  // Resolved tab for the pill highlight — server-derived falsy default
  // (false = we-recruit) until the client flips the switch.
  const activeTabWithoutHire = withoutHire ?? false;

  // An expired freemium job can still be upgraded, but the purchase only buys
  // access to the applicants it already gathered — the job is not republished.
  // Different promise, so different copy and no after-upgrade redirect.
  const isUnlockOnly = Boolean(upgradeBundlesData?.unlockOnly);

  const selectableBundles = useMemo(
    () => getDisplayBundlesForJobAddV2(
      (upgradeBundlesData?.bundles || []).filter((bundle) => bundle?.available && bundle?.selectable),
    ),
    [upgradeBundlesData?.bundles],
  );
  const [selectedPackageId, setSelectedPackageId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    const firstSelectablePackageId = selectableBundles[0]?.packageId;
    setSelectedPackageId((prev) => {
      if (prev && selectableBundles.some((bundle) => bundle.packageId === prev)) return prev;
      return firstSelectablePackageId;
    });
  }, [open, selectableBundles]);

  // Reset the tab override when the dialog closes so re-opening it on a
  // different job starts from "let server derive" again.
  useEffect(() => {
    if (!open) setWithoutHire(undefined);
  }, [open]);

  const selectedBundle = selectableBundles.find((bundle) => bundle.packageId === selectedPackageId);

  const selectedBundleId = selectedPackageId?.startsWith('bundle_')
    ? selectedPackageId.replace('bundle_', '')
    : selectedPackageId;

  const { trigger: triggerUpgrade, isMutating: isSubmitting } = useSwrMutationApi<
  ApiSchema<'JobPostingResponse2'>,
  undefined
  >({
    url: `/v1/my-jobs/${jobSlug}/upgrade/${selectedBundleId || ''}`,
    locale,
    fetcherConfig: {
      method: 'POST',
    },
    apiOptions: {
      onSuccess: (response) => {
        clearCacheKeysIncludes('/v1/my-jobs', true);
        onSuccess?.();
        onClose();

        if (response?.checkoutSessionUrl) {
          void NiceModal.show(RedirectCountdownModal, { url: response.checkoutSessionUrl, seconds: 5 });
          return;
        }

        // Nothing was republished, so /after-upgrade would promise a live
        // posting that isn't there. Refresh the applicant lists in place
        // instead — unlocking runs in the background and lands there.
        if (isUnlockOnly) {
          clearCacheKeysIncludes('/v1/job-operation', true);
          clearCacheKeysIncludes('/v3/job-basic-info', true);
          return;
        }

        const targetSlug = response?.slug || jobSlug;
        void router.push(`/after-upgrade/${targetSlug}`);
      },
    },
    toastsOptions: {
      showSuccessToast: true,
      successToastText: t(isUnlockOnly ? 'job.upgrade.unlock-only.success' : 'job.upgrade.success'),
      showErrorToast: true,
    },
  });

  const unavailableReason = (upgradeBundlesData?.bundles || []).find((bundle) => bundle?.unavailableReason)?.unavailableReason;

  const handleUpgrade = () => {
    if (!selectedBundleId || isSubmitting) return;

    void triggerUpgrade();
  };

  return (
    <Modal isOpen={open} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        <h5 className="text-xl">{t(isUnlockOnly ? 'job.upgrade.unlock-only.title' : 'job.upgrade.title')}</h5>
      </Modal.Header>

      <Modal.Body className="max-h-[65vh] overflow-y-auto">
        {isValidating && (
          <div className="flex items-center justify-center py-10">
            <TailwindSpinner className="size-6 text-secondary" />
          </div>
        )}

        {/* Why they are looking at this dialog at all. Rendered first and
            unconditionally on the reason — someone who clicked "contact" needs
            the connection to upgrading spelled out before they see prices.
            Falls back to the slug so the sentence never quotes an empty name. */}
        {!isValidating && reason === 'contact-applicant' && upgradeBundlesData?.jobTitle && (
          <p className="mb-4 text-sm text-ink">
            {t('job.upgrade.contact-applicant.description', { jobTitle: upgradeBundlesData.jobTitle })}
          </p>
        )}

        {!isValidating && isUnlockOnly && selectableBundles.length > 0 && (
          <p className="mb-4 text-sm text-ink-medium">
            {t('job.upgrade.unlock-only.description')}
          </p>
        )}

        {!isValidating && showHireTabs && (
          <div
            role="tablist"
            aria-label={t('job.bundle.selection.tab.we_recruit.label')}
            className="mb-4 inline-flex self-start rounded-full border border-input/70 bg-surface p-1 text-sm"
          >
            {([
              { key: 'we', value: false, label: 'job.bundle.selection.tab.we_recruit.label', sub: 'job.bundle.selection.tab.we_recruit.sub' },
              { key: 'you', value: true, label: 'job.bundle.selection.tab.you_recruit.label', sub: 'job.bundle.selection.tab.you_recruit.sub' },
            ] as const).map((tab) => {
              const isActive = activeTabWithoutHire === tab.value;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setWithoutHire(tab.value)}
                  className={`flex flex-col items-center rounded-full px-5 py-2 leading-tight transition-colors ${
                    isActive
                      ? 'bg-secondary text-surface'
                      : 'cursor-pointer text-ink-medium hover:text-ink'
                  }`}
                >
                  <span className="text-sm font-medium">{t(tab.label)}</span>
                  <span className={`text-xs ${isActive ? 'opacity-90' : 'opacity-70'}`}>{t(tab.sub)}</span>
                </button>
              );
            })}
          </div>
        )}

        {!isValidating && selectableBundles.length > 0 && (
          <div className="space-y-3">
            {selectableBundles.map((bundle) => {
              const isSelected = bundle.packageId === selectedPackageId;
              const displayPrice = toRoundedPrice(bundle.upgradeCredits ?? bundle.discountedCredits ?? bundle.credits);

              return (
                <button
                  key={bundle.packageId}
                  type="button"
                  onClick={() => setSelectedPackageId(bundle.packageId)}
                  className={`w-full rounded-2xl border p-4 text-left transition-colors ${
                    isSelected ? 'border-secondary bg-secondary/5' : 'border-input hover:bg-surface-50'
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <span className="font-semibold text-ink">{getBundleDisplayTitleV2(bundle, t)}</span>
                    <span className="text-sm font-bold text-secondary">
                      {displayPrice}
                      {' '}
                      {t('global.label.euro')}
                    </span>
                  </div>

                  {(bundle.benefits || []).length > 0 && (
                    <ul className="list-disc space-y-1 pl-4 text-sm text-ink-medium">
                      {(bundle.benefits || []).slice(0, 4).map((benefit) => {
                        const title = getBenefitTitle(benefit);
                        return title ? <li key={title}>{title}</li> : null;
                      })}
                    </ul>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {!isValidating && selectableBundles.length === 0 && (
          <p className="text-sm text-ink-medium">
            {unavailableReason || t('job.add.btn.no-upgrade')}
          </p>
        )}
      </Modal.Body>

      <Modal.Footer className="flex justify-end gap-2">
        <Button
          color="secondary"
          onClick={handleUpgrade}
          isLoading={isSubmitting}
          disabled={isSubmitting || !selectedBundle}
        >
          {t(isUnlockOnly ? 'job.upgrade.unlock-only.cta' : 'job.upgrade.title')}
        </Button>
        <Button color="light" onClick={onClose} disabled={isSubmitting}>
          {t('global.action.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface UpgradeJobModalProps {
  jobSlug: string;
  onSuccess?: () => void;
  reason?: UpgradeJobReason;
}

/**
 * @description Imperative wrapper around UpgradeJobDialog, for the places that
 * have no room to render it declaratively — notably the unlock flow, which has
 * to open it in reaction to an API error rather than a click.
 *
 * The dialog itself stays a controlled component so the existing declarative
 * usages (my-jobs card, company job actions) are unaffected.
 */
export const UpgradeJobModal = NiceModal.create((props: UpgradeJobModalProps) => {
  const { jobSlug, onSuccess, reason } = props;
  const modal = useModal();

  return (
    <UpgradeJobDialog
      open={modal.visible}
      onClose={() => { void modal.hide(); }}
      jobSlug={jobSlug}
      onSuccess={onSuccess}
      reason={reason}
    />
  );
});

interface ShowUpgradeJobModalOptions {
  onSuccess?: () => void;
  /** Set when the client did not come here to upgrade — see UpgradeJobReason. */
  reason?: UpgradeJobReason;
}

/**
 * @description Opens the job upgrade dialog anywhere in the app.
 *
 * @example
 * import { showUpgradeJobModal } from '@components/templates/my-jobs/UpgradeJobDialog';
 * showUpgradeJobModal('web-developer-12345');
 * showUpgradeJobModal(slug, { reason: 'contact-applicant', onSuccess });
 */
export const showUpgradeJobModal = (jobSlug: string, options: ShowUpgradeJobModalOptions = {}) => {
  const { onSuccess, reason } = options;

  void NiceModal.show(UpgradeJobModal, { jobSlug, onSuccess, reason });
};
